import React, { useEffect, useState } from "react";
import { Table, Spin, Space, Upload, Input, Modal, Button } from "antd";
import { serverRequest } from "../../util/server_request";
import { useNavigate } from "react-router-dom";
import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import {
  currencyFormatter,
  exportToCSV,
  exportToPDF,
  notifyUser,
} from "../../util/util";
import { PopupModal } from "../../components/PopupModal";
import { useValidationReportPDF } from "../../templates/ValidationReportTemplatePDF";
import { useTier3ValidationReportPDF } from "../../templates/Tier3ValidationReportTemplatePDF";
import { useProfiles } from "../../hooks/api/useProfile";

const { Dragger } = Upload;

const ScheduleUploadTable = (props) => {
  const downloadValidationReport = useValidationReportPDF();
  const downloadTier3ValidationReport = useTier3ValidationReportPDF();
  const navigate = useNavigate();
  const { data: profiles } = useProfiles();
  const activeProfile = profiles?.find((p) => p?.active) || profiles?.[0];
  const isTier3 = String(activeProfile?.tier) === "3";
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [originalData, setOriginalData] = useState([]);

  const [isModalVisible, setIsModalVisible] = useState(false);

  const [uploadSuccessModal, setUploadSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [validationModal, setValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [pinModal, setPinModal] = useState(false);
  const [pin, setPin] = useState("");
  const [uploadModal, setUploadModal] = useState(false);
  const [listSchedules, setListSchedules] = useState([]);
  const [listTotals, setListTotals] = useState({});
  const [scheduleMonth, setScheduleMonth] = useState("");
  const [scheduleSentState, setScheduleSentState] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);

  useEffect(() => {
    const fetchScheduleUploads = () => {
      toggleLoading();
      serverRequest("GET", "schedule/upload/months")
        .then((result) => {
          if (result) {
            toggleLoading();
            setUploads(result.data);
            setOriginalData(result.data);
          }
        })
        .catch((error) => {
          notifyUser("error", error.message, "Data fetching failed");
          toggleLoading();
        });
    };

    fetchScheduleUploads();
  }, []);

  const columns = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      onFilter: (value, record) => record.createdBy.includes(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      onFilter: (value, record) => record.createdBy.includes(value),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <button
          onClick={() =>
            navigate(`/dashboard/schedule/upload?month=${record.month}`)
          }
          className="btn btn-link text-decoration-none text-success p-0"
          style={{ cursor: "pointer" }}
        >
          View
        </button>
      ),
    },
  ];

  // Helper function to calculate expected 5% for salaries above 61k
  const calculateExpectedFivePer = (salary) => {
    if (salary >= 61000) {
      return 0.05 * salary + 0.135 * (salary - 61000);
    }
    return salary * 0.05;
  };

  // Updated display function for preview table - shows exact value from Excel without rounding
  const formatPercentageForDisplay = (value) => {
    if (
      !value ||
      value.toString().trim() === "" ||
      value.toString().trim() === "-" ||
      value.toString().trim() === "-  " ||
      value.toString().trim() === "--" ||
      value.toString().trim() === "- " ||
      value.toString().trim() === " -" ||
      value.toString().trim() === "0" ||
      value.toString().trim() === "0.00" ||
      parseFloat(value.toString().replace(/[^\d.-]/g, "")) === 0
    ) {
      return ""; // Display as empty for preview
    }
    // Use exact value from Excel - no rounding, just formatting
    return currencyFormatter(
      parseFloat(value.toString().replace(/[^\d.-]/g, "")),
      { round: false },
    );
  };

  const scheduleListColumns = [
    { title: "FIRST NAME", dataIndex: "FIRST NAME", key: "FIRST NAME" },
    { title: "LAST NAME", dataIndex: "LAST NAME", key: "LAST NAME" },
    { title: "OTHER NAME", dataIndex: "OTHER NAME", key: "OTHER NAME" },
    {
      title: "SALARY",
      dataIndex: "SALARY",
      key: "SALARY",
      render: (value) =>
        value
          ? currencyFormatter(parseFinancialNumber(value), { round: false })
          : "",
    },
    { title: "SSNIT NUMBER", dataIndex: "SSNIT NUMBER", key: "SSNIT NUMBER" },
    { title: "STAFF NUMBER", dataIndex: "STAFF NUMBER", key: "STAFF NUMBER" },
    {
      title: "5%",
      dataIndex: "5%",
      key: "FIVE PER",
      render: (value) => formatPercentageForDisplay(value),
    },
    {
      title: "18.50%",
      dataIndex: "18.50%",
      key: "EIGHTEEN PER",
      render: (value) => formatPercentageForDisplay(value),
    },
    { title: "GHANA CARD", dataIndex: "GHANA CARD", key: "GHANA CARD" },
  ];

  const scheduleListColumnsTier3 = [
    { title: "FIRST NAME", dataIndex: "First Name", key: "First Name" },
    { title: "LAST NAME", dataIndex: "Last Name", key: "Last Name" },
    { title: "OTHER NAMES", dataIndex: "Other Names", key: "Other Names" },
    { title: "STAFF NUMBER", dataIndex: "STAFF NUMBER", key: "STAFF NUMBER" },
    {
      title: "SALARY",
      dataIndex: "SALARY",
      key: "SALARY",
      render: (value) =>
        value
          ? currencyFormatter(parseFinancialNumber(value), { round: false })
          : "",
    },
    {
      title: "TOTAL CONTRIBUTION (%)",
      dataIndex: "Total Contribution (%)",
      key: "Total Contribution (%)",
      render: (value) => formatPercentageForDisplay(value),
    },
    { title: "SSNIT NUMBER", dataIndex: "SSNIT NUMBER", key: "SSNIT NUMBER" },
    { title: "GH NUMBER", dataIndex: "GH NUMBER", key: "GH NUMBER" },
  ];

  const toggleLoading = () => setLoading((prevState) => !prevState);
  const toggleUpload = () => setUploadModal((prevState) => !prevState);
  // const togglePinModal = () => setPinModal((prevState) => !prevState);
  // const toggleErrorModal = () => setErrorModal((prevState) => !prevState);
  const toggleScheduleSent = () =>
    setScheduleSentState((prevState) => !prevState);

  const closeUploadSuccessModal = () => {
    setUploadSuccessModal(false);
    setApiResponse(null);
  };

  // const closeAllModals = () => {
  //   setIsModalVisible(false);
  //   setUploadSuccessModal(false);
  //   setPinModal(false);
  //   setUploadModal(false);
  //   setErrorModal(false);
  //   setApiResponse(null);
  // };

  const fetchScheduleUploads = () => {
    toggleLoading();
    serverRequest("GET", "schedule/upload/months")
      .then((result) => {
        if (result) {
          toggleLoading();
          setUploads(result.data);
          setOriginalData(result.data);
        }
      })
      .catch((error) => {
        notifyUser("error", error.message, "Data fetching failed");
        toggleLoading();
      });
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);

    const filteredData = originalData.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(value),
      ),
    );
    setUploads(filteredData);
  };

  const downloadSample = () => {
    const link = document.createElement("a");
    if (isTier3) {
      link.href = "/SCHEDULE SAMPLE Tier3.xlsx";
      link.download = "SCHEDULE SAMPLE Tier3.xlsx";
    } else {
      link.href = "/SCHEDULE SAMPLE.xlsx";
      link.download = "SCHEDULE SAMPLE.xlsx";
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isEmptyRow = (row) =>
    Object.values(row).every((val) => !val?.toString().trim());

  const isSummaryRow = (row) => {
    const firstName = (row?.["FIRST NAME"] ?? "")
      .toString()
      .trim()
      .toLowerCase();
    const lastName = (row?.["LAST NAME"] ?? "").toString().trim().toLowerCase();
    return (
      firstName === "total" ||
      firstName === "amount due" ||
      (firstName ? firstName.includes("total") : false) ||
      (firstName ? firstName.includes("amount due") : false) ||
      lastName === "total" ||
      lastName === "amount due"
    );
  };

  const normalizeKeys = (row) => {
    const normalized = {};
    Object.keys(row).forEach((key) => {
      normalized[key.trim()] = row[key];
    });
    return normalized;
  };

  const parseNumber = (value) => {
    if (!value) return NaN;
    return parseFloat(value.toString().replace(/[^\d.-]/g, ""));
  };

  // Updated helper functions for better handling of 5% and 18.50% columns
  const parsePercentage = (value) => {
    if (
      !value ||
      value.toString().trim() === "" ||
      value.toString().trim() === "-" ||
      value.toString().trim() === "-  " ||
      value.toString().trim() === "--" ||
      value.toString().trim() === "- " ||
      value.toString().trim() === " -" ||
      value.toString().trim() === "0" ||
      value.toString().trim() === "0.00" ||
      parseFloat(value.toString().replace(/[^\d.-]/g, "")) === 0
    ) {
      return null; // Return null for empty, dash variations, or zero values
    }
    return parseFloat(value.toString().replace(/[^\d.-]/g, ""));
  };

  const parseFinancialNumber = (value) => {
    if (
      !value ||
      value.toString().trim() === "" ||
      value.toString().trim() === "-" ||
      value.toString().trim() === "-  " ||
      value.toString().trim() === "--" ||
      value.toString().trim() === "- " ||
      value.toString().trim() === " -" ||
      value.toString().trim() === "0" ||
      value.toString().trim() === "0.00"
    ) {
      return ""; // Return empty string for empty, dash variations, or zero values
    }
    return parseFloat(value.toString().replace(/,/g, "")) || "";
  };

  // Additional helper for submission data - returns empty string instead of 0
  // eslint-disable-next-line no-unused-vars
  const parsePercentageForSubmission = (value) => {
    if (
      !value ||
      value.toString().trim() === "" ||
      value.toString().trim() === "-" ||
      value.toString().trim() === "-  " ||
      value.toString().trim() === "0" ||
      parseFloat(value.toString().replace(/[^\d.-]/g, "")) === 0
    ) {
      return ""; // Return empty string for submission when empty, dash, or zero
    }
    return parseFloat(value.toString().replace(/[^\d.-]/g, ""));
  };

  // Helper function to convert Excel percentage values to actual amounts
  const convertExcelPercentageToAmount = (value, salary) => {
    if (!value || !salary || isNaN(salary)) return value;

    const stringValue = value.toString().trim();

    // Handle empty or dash values (various dash formats)
    if (
      stringValue === "" ||
      stringValue === "-" ||
      stringValue === "-  " ||
      stringValue === "--" ||
      stringValue === "- " ||
      stringValue === " -" ||
      stringValue === "0" ||
      stringValue === "0.00"
    ) {
      return "";
    }

    // Handle percentage text like "5%" or "18.5%" - convert to decimal equivalent amount
    if (stringValue.includes("%")) {
      const percentageNum = parseFloat(stringValue.replace("%", ""));
      if (!isNaN(percentageNum)) {
        return salary * (percentageNum / 100);
      }
    }

    // Handle decimal values from Excel (e.g., 0.05 for 5%, 0.185 for 18.5%)
    const numValue = parseFloat(stringValue);
    if (!isNaN(numValue)) {
      // If the value is less than 1, assume it's a decimal percentage (e.g., 0.05 = 5%)
      if (numValue < 1 && numValue > 0) {
        return salary * numValue;
      }
      // If the value is greater than 1, assume it's already an amount
      return numValue;
    }

    return value;
  };

  const validateExcelFile = (data) => {
    const errors = [];
    const ssnitNumbers = new Set();

    const requiredColumns = [
      "FIRST NAME",
      "LAST NAME",
      "SSNIT NUMBER",
      "SALARY",
      "GHANA CARD",
    ];
    const missingColumns = requiredColumns.filter((col) => !data[0]?.[col]);
    if (missingColumns.length > 0) {
      notifyUser(
        "error",
        `Missing required columns: ${missingColumns.join(", ")}`,
        "File Validation Failed",
      );
      return { isValid: false, errors };
    }

    data.forEach((row, index) => {
      const rowNumber = index + 2;

      if (isEmptyRow(row)) return;
      if (isSummaryRow(row)) return;

      const missingFields = [];
      if (!row["FIRST NAME"]?.toString().trim())
        missingFields.push("First Name");
      if (!row["LAST NAME"]?.toString().trim()) missingFields.push("Last Name");
      if (!row["SSNIT NUMBER"]?.toString().trim())
        missingFields.push("SSNIT Number");
      if (!row["GHANA CARD"]?.toString().trim())
        missingFields.push("Ghana Card");

      if (missingFields.length > 0) {
        errors.push(
          `Row ${rowNumber}: Missing required fields - ${missingFields.join(", ")}`,
        );
        return;
      }

      // Validate Ghana Card format
      const ghanaCard = row["GHANA CARD"].toString().trim();
      if (!/^GHA-\d{9}-\d$/.test(ghanaCard)) {
        errors.push(
          `Row ${rowNumber}: Ghana Card must be in format GHA-xxxxxxxxx-x`,
        );
      }

      const ssnit = row["SSNIT NUMBER"].toString().trim();
      if (ssnitNumbers.has(ssnit)) {
        errors.push(`Row ${rowNumber}: Duplicate SSNIT Number (${ssnit})`);
      } else {
        ssnitNumbers.add(ssnit);
      }

      const salary = parseNumber(row["SALARY"]);
      if (isNaN(salary)) {
        errors.push(`Row ${rowNumber}: SALARY must be a valid number`);
        return;
      }

      const fivePercent = parsePercentage(row["5%"]);
      const eighteenPercent = parsePercentage(row["18.50%"]);

      // Updated validation logic for different salary ranges
      if (salary >= 61000) {
        // For salaries ≥61k, 5% is auto-calculated by the system, so skip validation
        // Just check that 18.50% is not provided (system will clear it anyway)
        if (eighteenPercent !== null) {
          errors.push(
            `Row ${rowNumber}: For salaries ≥61,000, 18.50% should be empty (5% will be auto-calculated)`,
          );
        }
      } else {
        // For salaries below 61k, ensure both 5% and 18.50% cannot be filled simultaneously
        if (fivePercent !== null && eighteenPercent !== null) {
          errors.push(
            `Row ${rowNumber}: You cannot have both 5% and 18.50% filled at the same time. Please provide only one.`,
          );
          return;
        }

        // For salaries below 61k, at least one percentage should be provided
        if (fivePercent === null && eighteenPercent === null) {
          errors.push(
            `Row ${rowNumber}: For salaries below 61,000, either 5% or 18.50% must be provided`,
          );
        }

        // Validate percentage calculations for salaries below 61k
        if (fivePercent !== null && Math.abs(fivePercent - salary * 0.05) > 1) {
          errors.push(
            `Row ${rowNumber}: 5% should be GH₵${(salary * 0.05).toFixed(2)} (found GH₵${fivePercent.toFixed(2)})`,
          );
        }

        if (
          eighteenPercent !== null &&
          Math.abs(eighteenPercent - salary * 0.185) > 1
        ) {
          errors.push(
            `Row ${rowNumber}: 18.50% should be GH₵${(salary * 0.185).toFixed(2)} (found GH₵${eighteenPercent.toFixed(2)})`,
          );
        }
      }
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      setValidationModal(true);
    }

    return { isValid: errors.length === 0, errors };
  };

  const validateExcelFileTier3 = (data) => {
    const errors = [];
    const ssnitNumbers = new Set();

    const requiredColumns = [
      "Last Name",
      "First Name",
      "STAFF NUMBER",
      "SALARY",
      "Total Contribution (%)",
      "SSNIT NUMBER",
      "GH NUMBER",
    ];
    const firstRow = data?.[0] || {};
    const missingColumns = requiredColumns.filter(
      (col) => !Object.prototype.hasOwnProperty.call(firstRow, col),
    );
    if (missingColumns.length > 0) {
      notifyUser(
        "error",
        `Missing required columns: ${missingColumns.join(", ")}`,
        "File Validation Failed",
      );
      return { isValid: false, errors };
    }

    data.forEach((row, index) => {
      const rowNumber = index + 2;

      if (isEmptyRow(row)) return;

      const missingFields = [];
      if (!row["First Name"]?.toString().trim())
        missingFields.push("First Name");
      if (!row["Last Name"]?.toString().trim()) missingFields.push("Last Name");

      const staffNumber = row["STAFF NUMBER"]?.toString().trim() || "";
      const ssnit = row["SSNIT NUMBER"]?.toString().trim() || "";
      const ghNumber = row["GH NUMBER"]?.toString().trim() || "";

      // Tier 3 identifier rule: staff number OR SSNIT number must be provided
      if (!staffNumber && !ssnit) {
        missingFields.push("STAFF NUMBER or SSNIT NUMBER");
      }

      if (missingFields.length > 0) {
        errors.push(
          `Row ${rowNumber}: Missing required fields - ${missingFields.join(", ")}`,
        );
        return;
      }

      // Validate GH Number format (when provided)
      if (ghNumber && !/^GHA-\d{9}-\d$/.test(ghNumber)) {
        errors.push(`Row ${rowNumber}: GH Number must be in format GHA-xxxxxxxxx-x`);
      }

      if (ssnit) {
        if (ssnitNumbers.has(ssnit)) {
          errors.push(`Row ${rowNumber}: Duplicate SSNIT Number (${ssnit})`);
        } else {
          ssnitNumbers.add(ssnit);
        }
      }

      const salary = parseNumber(row["SALARY"]);
      if (isNaN(salary)) {
        errors.push(`Row ${rowNumber}: SALARY must be a valid number`);
      }

      const totalPercent = parseNumber(row["Total Contribution (%)"]);
      if (isNaN(totalPercent)) {
        errors.push(
          `Row ${rowNumber}: Total Contribution (%) must be a valid number`,
        );
      } else if (totalPercent < 0 || totalPercent > 100) {
        errors.push(
          `Row ${rowNumber}: Total Contribution (%) must be between 0 and 100`,
        );
      }
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      setValidationModal(true);
    }

    return { isValid: errors.length === 0, errors };
  };

  const handleUpload = (file) => {
    const isExcel =
      file.type === "application/vnd.ms-excel" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isExcel) {
      notifyUser(
        "error",
        "You can only upload Excel files (.xls or .xlsx)!",
        "Error",
      );
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        let json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        if (isTier3) {
          // Normalize keys by trimming whitespace for tier3
          json = json.map((row) => normalizeKeys(row));
          json = json.filter(
            (row) =>
              !(
                row["First Name"] === undefined &&
                row["Last Name"] === undefined &&
                row["SSNIT NUMBER"] === undefined
              ),
          );

          const validationResult = validateExcelFileTier3(json);
          if (!validationResult.isValid) {
            return;
          }

          setListSchedules(json);
          setIsModalVisible(true);
          toggleUpload();
          const totals = calculateTotals(json);
          setListTotals({
            totalContributions: totals.totalContributions,
            totalSalary: totals.totalSalary,
          });
        } else {
          // Tier 2 flow (existing)
          json = json.filter(
            (row) =>
              !(
                row["FIRST NAME"] === undefined &&
                row["LAST NAME"] === undefined &&
                row["SSNIT NUMBER"] === undefined
              ),
          );

          // Process data to handle Excel percentage formatting and auto-calculate FIVE PER for salaries 61k and above
          const processedJson = json.map((row) => {
            if (isEmptyRow(row) || isSummaryRow(row)) {
              return row;
            }

            // Handle Excel percentage formatting for 5% and 18.50% columns
            if (row["5%"]) {
              row["5%"] = convertExcelPercentageToAmount(
                row["5%"],
                parseNumber(row["SALARY"]),
              );
            }

            if (row["18.50%"]) {
              row["18.50%"] = convertExcelPercentageToAmount(
                row["18.50%"],
                parseNumber(row["SALARY"]),
              );
            }

            const salary = parseNumber(row["SALARY"]);
            if (!isNaN(salary) && salary >= 61000) {
              // Auto-calculate 5% for salaries 61k and above
              const calculatedFivePer = calculateExpectedFivePer(salary);
              row["5%"] = calculatedFivePer;
              // Clear 18.50% for salaries above 61k
              row["18.50%"] = "";
            }

            return row;
          });

          const validationResult = validateExcelFile(processedJson);

          if (!validationResult.isValid) {
            return;
          }

          setListSchedules(processedJson);
          setIsModalVisible(true);
          toggleUpload();
          const totals = calculateTotals(processedJson);
          setListTotals({
            totalContributions: totals.totalFivePer + totals.totalEighteenPer,
            totalFivePer: totals.totalFivePer,
            totalEighteenPer: totals.totalEighteenPer,
            totalSalary: totals.totalSalary,
          });
        }
      } catch (error) {
        notifyUser(
          "error",
          "Failed to process the Excel file. Please ensure it's properly formatted.",
          "Error",
        );
        console.error("Error processing file:", error);
      }
    };

    reader.onerror = () => {
      notifyUser(
        "error",
        "Failed to read the file. Please try again.",
        "Error",
      );
    };

    reader.readAsArrayBuffer(file);
    return false;
  };

  // Updated calculateTotals function to handle empty strings
  const calculateTotals = (data) => {
    if (isTier3) {
      return data.reduce(
        (acc, row) => {
          if (isEmptyRow(row)) {
            return acc;
          }

          const salary = parseFinancialNumber(row["SALARY"]);
          const totalPercent = parseFinancialNumber(
            row["Total Contribution (%)"],
          );

          const salaryNum = salary === "" ? 0 : salary;
          const totalPercentNum = totalPercent === "" ? 0 : totalPercent;
          const contributionAmount =
            !isNaN(totalPercentNum) && totalPercentNum !== 0
              ? (salaryNum * totalPercentNum) / 100
              : 0;

          return {
            totalContributions: acc.totalContributions + contributionAmount,
            totalSalary: acc.totalSalary + salaryNum,
          };
        },
        { totalContributions: 0, totalSalary: 0 },
      );
    }

    return data.reduce(
      (acc, row) => {
        if (isSummaryRow(row) || isEmptyRow(row)) {
          return acc;
        }

        const salary = parseFinancialNumber(row["SALARY"]);
        const fivePer = parseFinancialNumber(row["5%"]);
        const eighteenPer = parseFinancialNumber(row["18.50%"]);

        // Convert empty strings to 0 for calculation
        const salaryNum = salary === "" ? 0 : salary;
        const fivePerNum = fivePer === "" ? 0 : fivePer;
        const eighteenPerNum = eighteenPer === "" ? 0 : eighteenPer;

        return {
          totalFivePer: acc.totalFivePer + fivePerNum,
          totalEighteenPer: acc.totalEighteenPer + eighteenPerNum,
          totalSalary: acc.totalSalary + salaryNum,
        };
      },
      { totalFivePer: 0, totalEighteenPer: 0, totalSalary: 0 },
    );
  };

  const verifyPinAndDownload = () => {
    if (isTier3) {
      downloadTier3ValidationReport(apiResponse);
    } else {
      downloadValidationReport(apiResponse);
    }
    setPinModal(false);
  };

  const exportFunction = (type) => {
    if (type === "pdf") {
      exportToPDF(columns, uploads, "Schedule Uploads");
    } else {
      exportToCSV(uploads, "Schedule Uploads");
    }
  };

  // Updated member mapping for submission
  const submitUpload = () => {
    if (scheduleMonth === "" || scheduleMonth == null) {
      return notifyUser(
        "error",
        "Please select schedule date to proceed!",
        "Validation",
      );
    }

    toggleScheduleSent();
    const fundPeriod = scheduleMonth.split("-");

    const validMembers = listSchedules.filter(
      (row) => !isEmptyRow(row) && !isSummaryRow(row),
    );

    // Helper to safely get trimmed string or empty string
    const safe = (v) => (v === undefined || v === null ? "" : String(v).trim());

    // Convert input to number or empty string (handles "-" as empty)
    const toBigDecimalOrEmpty = (value) => {
      if (
        !value ||
        value.toString().trim() === "" ||
        value.toString().trim() === "-"
      ) {
        return "";
      }
      const num = parseFloat(value.toString().replace(/[^\d.-]/g, ""));
      return isNaN(num) ? "" : num;
    };

    let payload;

    if (isTier3) {
      const _members = validMembers.map((item) => ({
        firstName: safe(item["First Name"]),
        lastName: safe(item["Last Name"]),
        otherName: safe(item["Other Names"]),
        ssnitNumber: safe(item["SSNIT NUMBER"]),
        salary: toBigDecimalOrEmpty(item["SALARY"]),
        totalContributionPercent: toBigDecimalOrEmpty(
          item["Total Contribution (%)"],
        ),
        preTaxEmployeeAmount: null,
        preTaxEmployerAmount: null,
        postTaxEmployeeAmount: null,
        postTaxEmployerAmount: null,
        staffNumber: safe(item["STAFF NUMBER"]),
        ghanaCardNumber: safe(item["GH NUMBER"]),
        fivePercent: null,
        eighteenPercent: null,
      }));

      payload = {
        fundYear: parseInt(fundPeriod[0], 10),
        fundMonth: fundPeriod[1],
        members: _members,
      };
    } else {
      const _members = validMembers.map((item) => ({
        firstName: safe(item["FIRST NAME"]),
        lastName: safe(item["LAST NAME"]),
        otherName: safe(item["OTHER NAME"]),
        ssnitNumber: safe(item["SSNIT NUMBER"]),
        salary: toBigDecimalOrEmpty(item["SALARY"]),
        postTaxEmployerAmount: null,
        preTaxEmployerAmount: null,
        //staffNumber: safe(item["STAFF NUMBER"]),
        ghanaCardNumber: safe(item["GHANA CARD"]),
        fivePercent: toBigDecimalOrEmpty(item["5%"]),
        eighteenPercent: toBigDecimalOrEmpty(item["18.50%"]),
      }));

      payload = {
        fundYear: parseInt(fundPeriod[0], 10),
        fundMonth: fundPeriod[1],
        tier: "TIER_2",
        members: _members,
      };
    }

    serverRequest("POST", "schedule/upload", payload)
      .then((result) => {
        const combinedResponse = {
          ...result,
          requestData: {
            fundYear: parseInt(payload.fundYear, 10),
            fundMonth: parseInt(payload.fundMonth, 10),
            members: payload.members.map((m) => ({
              firstName: m.firstName ?? "",
              lastName: m.lastName ?? "",
              salary: m.salary ?? "",
              fivePercent: m.fivePercent ?? "",
              eighteenPercent: m.eighteenPercent ?? "",
              ssnitNumber: m.ssnitNumber ?? "",
              staffNumber: m.staffNumber ?? "",
              ghanaNumber: m.ghanaCardNumber ?? "",
              ghanaCardNumber: m.ghanaCardNumber ?? "",
            })),
            totalFivePercent: payload.members.reduce(
              (sum, m) => sum + (m.fivePercent || 0),
              0,
            ),
            totalEighteenPercent: payload.members.reduce(
              (sum, m) => sum + (m.eighteenPercent || 0),
              0,
            ),
            totalSalary: payload.members.reduce(
              (sum, m) => sum + (m.salary || 0),
              0,
            ),
          },
        };

        setApiResponse(combinedResponse);
        toggleScheduleSent();

        if (!result?.success) {
          notifyUser("error", result?.message, "Error");
          return;
        }

        const hasErrors =
          result?.data?.errors &&
          Array.isArray(result.data.errors) &&
          result.data.errors.length > 0;

        if (hasErrors) {
          setIsModalVisible(false);
          setErrorModal(true);
          toggleUpload();
        } else {
          notifyUser(
            "success",
            "Data uploaded successfully!",
            "Schedule Upload",
          );
          setIsModalVisible(false);
          setUploadSuccessModal(true);
          toggleUpload();
        }
      })
      .catch((error) => {
        toggleScheduleSent();
        notifyUser("error", error.message, "Upload Failed");
      });
  };
  return (
    <div style={{ padding: "20px" }}>
      <div className="row mb-4">
        <div className="col">
          <p className="lead">Your Pension Contributions</p>
        </div>
        <div className="col"></div>
      </div>
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-3">
          <div className="shadow p-4 rounded">
            <p className="lead mb-1">{uploads?.length}</p>
            <small>Total Uploads</small>
            <div className="mt-2">
              <span>
                <span className="badge bg-success fw-bold">
                  <i className="bi bi-cloud-upload" />
                </span>
              </span>
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 mb-3">
          <div className="shadow p-4 rounded">
            <p className="lead mb-1">
              {uploads?.filter((item) => item.status !== "UPLOADED")?.length}
            </p>
            <small>Failed Uploads</small>
            <div className="mt-2">
              <span>
                <span
                  className="badge fw-bold"
                  style={{ backgroundColor: "#ffe5e5", color: "#FF0000" }}
                >
                  <i className="bi bi-graph-down-arrow" />
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <Space
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <p className="lead">Schedule Uploads</p>
          <Space>
            <button className="btn btn-primary" onClick={toggleUpload}>
              Upload <i className="bi bi-cloud-upload" />
            </button>
            <div className="dropdown">
              <Button
                icon={<DownloadOutlined />}
                className="dropdown-toggle"
                id="dropdownMenuButton1"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                EXPORT
              </Button>
              <ul
                className="dropdown-menu"
                aria-labelledby="dropdownMenuButton1"
              >
                <li>
                  <button
                    className="dropdown-item"
                    type="button"
                    onClick={() => exportFunction("csv")}
                  >
                    Export to CSV
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    type="button"
                    onClick={() => exportFunction("pdf")}
                  >
                    Export to PDF
                  </button>
                </li>
              </ul>
            </div>
            <Input
              prefix={<SearchOutlined />}
              placeholder="SEARCH"
              style={{ width: 200 }}
              value={searchText}
              onChange={handleSearch}
              allowClear
            />
          </Space>
        </Space>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={uploads}
            pagination={{ pageSize: 10 }}
            bordered
            loading={loading}
          />
        </Spin>
      </div>

      {/* Upload Modal */}
      <Modal
        visible={uploadModal}
        onCancel={toggleUpload}
        footer={null}
        width={650}
      >
        <div className="p-4">
          <h2 className="fw-bold">Upload Schedule</h2>
          <p className="font-weight-light mb-2">
            Upload the pension schedule, firstly download the sample file below.
          </p>
          <p
            className="font-weight-light mb-5 text-success fw-bold"
            style={{ cursor: "pointer" }}
            onClick={downloadSample}
          >
            {isTier3 ? "Download Tier 3 Sample.xlsx" : "Download Sample.xlsx"}
          </p>
          <Dragger
            beforeUpload={handleUpload}
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            maxCount={1}
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon">
              <i className="bi bi-cloud-upload" />
            </p>
            <p className="ant-upload-text">Schedule Upload</p>
            <p className="ant-upload-hint">Max 10MB</p>
          </Dragger>
        </div>
      </Modal>

      {/* Schedule Summary Modal */}
      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={1200}
      >
        <div className="p-3">
          <h2 className="fw-bold">Schedule Summary</h2>
          <p className="">See your schedule summary</p>

          {isTier3 ? (
            <>
              <p className="fw-bold">
                Total Salary : GH₵
                {currencyFormatter(listTotals?.totalSalary, { round: false })}
              </p>
              <p className="fw-bold">
                Total Contributions : GH₵
                {currencyFormatter(listTotals?.totalContributions, {
                  round: false,
                })}
              </p>
            </>
          ) : (
            <>
              <p className="fw-bold">
                Total Five Per: GH₵
                {currencyFormatter(listTotals?.totalFivePer, { round: false })}
              </p>
              <p className="fw-bold">
                Total Eighteen Per: GH₵
                {currencyFormatter(listTotals?.totalEighteenPer, {
                  round: false,
                })}
              </p>
              <p className="fw-bold">
                Total Salary : GH₵
                {currencyFormatter(listTotals?.totalSalary, { round: false })}
              </p>
              <p className="fw-bold">
                Total Contributions : GH₵
                {currencyFormatter(listTotals?.totalContributions, {
                  round: false,
                })}
              </p>
            </>
          )}

          <div className="">
            <Spin spinning={scheduleSentState}>
              <Table
                columns={
                  isTier3 ? scheduleListColumnsTier3 : scheduleListColumns
                }
                dataSource={listSchedules.filter(
                  (row) => !isSummaryRow(row) && !isEmptyRow(row),
                )}
                bordered
                pagination={{ pageSize: 10 }}
              />
              <div className="row align-items-center mt-4">
                <div className="col-lg-6 col-md-6 col-xs-12 col-sm-12">
                  <omk-date-picker
                    label="Select Schedule Date"
                    value={scheduleMonth}
                    type="date"
                    className="mb-4"
                    name="scheduleMonth"
                    onInput={(e) => setScheduleMonth(e.target.value)}
                  />
                </div>
                <div className="col-lg-6 col-md-6 col-xs-12 col-sm-12 text-end">
                  <button
                    className="btn btn-primary w-50 mobile-full-width"
                    onClick={submitUpload}
                    disabled={scheduleSentState}
                  >
                    {scheduleSentState ? "Uploading..." : "Submit"}
                  </button>
                </div>
              </div>
            </Spin>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <PopupModal
        title="Schedule Uploaded Successfully!"
        subTitle={
          <div className="d-flex flex-column">
            <span>Your Upload has been made successfully</span>
            <div className="d-flex gap-3 mt-4">
              <button
                className="btn btn-primary flex-grow-1"
                onClick={() => {
                  closeUploadSuccessModal();
                  fetchScheduleUploads();
                }}
              >
                CONTINUE
              </button>
              <button
                className="btn btn-primary flex-grow-1"
                onClick={() => {
                  if (isTier3) {
                    downloadTier3ValidationReport(apiResponse);
                  } else {
                    downloadValidationReport(apiResponse);
                  }
                }}
              >
                Download Report
              </button>
            </div>
          </div>
        }
        isOpen={uploadSuccessModal}
        closeFn={() => {
          closeUploadSuccessModal();
          fetchScheduleUploads();
        }}
        closeText={null} // Hide the default button
      />
      {/* Error Modal */}
      <Modal
        open={errorModal}
        onCancel={() => setErrorModal(false)}
        footer={null}
        width={700}
      >
        <div className="p-4">
          <div className="text-center mb-4">
            <div className="mb-3">
              <span className="badge bg-danger p-3 rounded-circle">
                <i
                  className="bi bi-exclamation-triangle-fill"
                  style={{ fontSize: "2rem" }}
                />
              </span>
            </div>
            <h4 className="text-danger">Upload Contains Validation Errors</h4>
            <p className="text-muted">
              Your schedule was not uploaded it contains validation errors.
              Please add missing members or fix the errors below, then upload a
              corrected file.
            </p>
          </div>

          {apiResponse?.data?.errors && (
            <div className="mb-4">
              <h6>Error Details:</h6>
              <div
                className="border rounded p-3"
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {apiResponse.data.errors.map((error, i) => {
                  const staff = (error?.staffNumber ?? "").toString().trim();
                  const ssnit = (error?.ssnitNumber ?? "").toString().trim();
                  const label = staff
                    ? "Staff Number"
                    : ssnit
                      ? "SSNIT Number"
                      : "Identifier";
                  const value = staff || ssnit || "-";

                  return (
                    <div key={i} className="mb-2">
                      <strong>{label}:</strong> {value}
                      <br />
                      <span className="text-danger">{error.errorMessage}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="text-center mb-3">
            <div className="alert alert-warning" role="alert">
              <i className="bi bi-info-circle-fill me-2"></i>
              <strong>Action Required:</strong> Please fix the validation errors
              above and upload your file again. The download report feature is
              only available for successfully uploaded schedules.
            </div>
          </div>

          <div className="d-flex justify-content-center gap-3">
            <button
              className="btn btn-primary"
              onClick={() => setErrorModal(false)}
            >
              Close & Fix Errors
            </button>
            {apiResponse?.data?.errors?.some((error) => {
              const errorMsg = error.errorMessage?.toLowerCase() || "";
              return (
                // Check for member-related existence errors
                (errorMsg.includes("member") &&
                  (errorMsg.includes("doesn't exist") ||
                    errorMsg.includes("does not exist") ||
                    errorMsg.includes("not found") ||
                    errorMsg.includes("not exist"))) ||
                // Check for staff number related errors that might indicate missing members
                (errorMsg.includes("staff") &&
                  (errorMsg.includes("not found") ||
                    errorMsg.includes("does not exist") ||
                    errorMsg.includes("doesn't exist"))) ||
                // Check for SSNIT number related errors
                (errorMsg.includes("ssnit") &&
                  (errorMsg.includes("not found") ||
                    errorMsg.includes("does not exist") ||
                    errorMsg.includes("doesn't exist")))
              );
            }) && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  setErrorModal(false);
                  navigate("/dashboard/members/new");
                }}
              >
                <i className="bi bi-person-plus me-2"></i>
                Create Members
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* PIN Modal */}
      <Modal
        open={pinModal}
        onCancel={() => setPinModal(false)}
        footer={null}
        width={600}
      >
        <div align={"center"}>
          <div className="p-4">
            <div className="mb-4">
              <h4>Enter PIN to download validation report</h4>
              <Input.Password
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                style={{ width: 200 }}
                maxLength={4}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={verifyPinAndDownload}
              disabled={pin.length !== 4}
            >
              Confirm & Download
            </button>
          </div>
        </div>
      </Modal>

      {/* Validation Errors Modal - Human Friendly */}
      <Modal
        open={validationModal}
        onCancel={() => setValidationModal(false)}
        footer={null}
        width={600}
        centered
      >
        <div className="p-4 text-center">
          {/* Icon */}
          <div className="mb-3">
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: 70,
                height: 70,
                backgroundColor: "#ffe5e5",
              }}
            >
              <i
                className="bi bi-exclamation-triangle-fill text-danger"
                style={{ fontSize: "2rem" }}
              />
            </span>
          </div>

          {/* Title */}
          <h4 className="text-danger mb-2">File Validation Failed</h4>

          {/* Subtitle */}
          <p className="text-muted mb-4">
            We found {validationErrors.length} issue
            {validationErrors.length > 1 ? "s" : ""} in your upload. Please fix{" "}
            {validationErrors.length > 1 ? "them" : "it"} before trying again.
          </p>

          {/* Error List */}
          <div
            className="text-start border rounded p-3 mb-4"
            style={{
              maxHeight: "200px",
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
            }}
          >
            {validationErrors.map((error, i) => (
              <div
                key={i}
                className="mb-2 pb-2 border-bottom"
                style={{ borderColor: "#dee2e6" }}
              >
                <i className="bi bi-x-circle-fill text-danger me-2" />
                <span>{error}</span>
              </div>
            ))}
          </div>

          {/* Help Tips */}
          <div className="alert alert-light border text-start mb-4">
            <strong>
              <i className="bi bi-lightbulb me-2 text-warning" />
              How to fix:
            </strong>
            <ul className="mb-0 mt-2 ps-3">
              <li>Open your Excel file</li>
              <li>Find the row mentioned above</li>
              <li>Correct the value</li>
              <li>Save and upload again</li>
            </ul>
          </div>

          {/* Action Button */}
          <button
            className="btn btn-primary w-100"
            onClick={() => setValidationModal(false)}
          >
            <i className="bi bi-arrow-left me-2" />
            Go Back & Fix
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ScheduleUploadTable;
